<script setup lang="ts">
// Recent spins shown to the player on this device. The server is the
// authoritative spin log — this is just a UX convenience that survives
// reloads without needing a network round-trip.
import { ref, onMounted } from 'vue';
import { useGameStore } from '../stores/game';
import { syncNow } from '../lib/offlineSync';
import type { SpinResult } from '../lib/api';

defineEmits<{ (e: 'close'): void }>();

const store = useGameStore();
const cache = ref<SpinResult[]>([]);
const syncBusy = ref(false);
const syncMsg = ref<string | null>(null);
const KEY = 'wsb_recent_spins';

async function doSync() {
  if (syncBusy.value) return;
  syncBusy.value = true;
  syncMsg.value = null;
  try {
    const res = await syncNow();
    if (!res.online) syncMsg.value = 'Offline — will sync when online.';
    else if (res.synced === 0 && res.ticketsFetched === 0) syncMsg.value = 'Already up to date.';
    else {
      const bits: string[] = [];
      if (res.synced) bits.push(`${res.synced} spin${res.synced === 1 ? '' : 's'} synced`);
      if (res.ticketsFetched) bits.push(`${res.ticketsFetched} tickets fetched`);
      syncMsg.value = bits.join(' · ');
    }
  } catch (e: any) {
    syncMsg.value = 'Sync failed — try again.';
  } finally {
    syncBusy.value = false;
    setTimeout(() => (syncMsg.value = null), 4000);
  }
}

onMounted(() => {
  try {
    cache.value = JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch {
    cache.value = [];
  }
});

function fmt(ts: number) {
  const d = new Date(ts * 1000);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
</script>

<template>
  <div class="bg" @click="$emit('close')"></div>
  <div class="panel">
    <header>
      <div class="ttl">Recent Spins</div>
      <div class="actions">
        <button class="btn btn-sync" :disabled="syncBusy || !store.online" @click="doSync">
          {{ syncBusy ? 'Syncing…' : 'Sync Now' }}
        </button>
        <button class="x" @click="$emit('close')">×</button>
      </div>
    </header>
    <div v-if="syncMsg" class="sync-msg">{{ syncMsg }}</div>
    <div v-if="!cache.length" class="empty">No spins yet on this device.</div>
    <ul v-else class="list">
      <li v-for="(s, i) in cache.slice(0, 10)" :key="i" :class="{ won: s.won }">
        <span class="t">{{ fmt(s.server_time) }}</span>
        <span class="r">{{ s.won ? 'WIN' : 'NO WIN' }}</span>
        <span class="p">{{ s.prize?.name ?? '—' }}</span>
      </li>
    </ul>
    <footer>
      <span :class="['dot', store.online ? 'on' : 'off']"></span>
      <span>{{ store.online ? 'Online' : 'Offline' }} · {{ store.ticketsRemaining }} ticket{{ store.ticketsRemaining === 1 ? '' : 's' }} buffered</span>
      <span v-if="store.redemptionsPending > 0" class="pending">· {{ store.redemptionsPending }} pending sync</span>
    </footer>
  </div>
</template>

<style scoped>
.bg {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 1000;
}
.panel {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--dark2);
  border: 1px solid var(--surf3);
  border-radius: var(--r);
  width: 92%;
  max-width: 420px;
  z-index: 1001;
  box-shadow: var(--sh);
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--surf3);
}
.ttl {
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
}
.actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.btn-sync {
  background: var(--or);
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
}
.btn-sync:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.x {
  background: none;
  border: none;
  color: var(--white);
  font-size: 22px;
  cursor: pointer;
}
.sync-msg {
  padding: 8px 18px;
  font-size: 12px;
  color: var(--green);
  background: rgba(40, 170, 35, 0.08);
  border-bottom: 1px solid var(--surf3);
}
.empty {
  padding: 28px;
  text-align: center;
  color: var(--grey);
  font-size: 13px;
}
.list {
  list-style: none;
  overflow-y: auto;
  padding: 6px 0;
}
.list li {
  display: grid;
  grid-template-columns: 72px 72px 1fr;
  align-items: center;
  padding: 10px 18px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
}
.list li.won .r {
  color: var(--green);
  font-weight: 700;
}
.list li .r {
  color: var(--grey);
}
.list li .t {
  color: var(--grey);
}
footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  font-size: 11px;
  color: var(--grey);
  border-top: 1px solid var(--surf3);
}
footer .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
footer .dot.on {
  background: var(--green);
  box-shadow: 0 0 6px var(--green);
}
footer .dot.off {
  background: #ffcb45;
  box-shadow: 0 0 6px #ffcb45;
}
footer .pending {
  color: #ffcb45;
}
</style>
